import React, { useEffect, useState, useRef } from "react";
import { X, Sparkles, Trophy } from "lucide-react";
import api from "../lib/api.js";
import { chime, goalConfetti } from "../lib/celebrate.js";

/**
 * Daily Spin-the-Wheel modal.
 *
 * Renders a colourful wheel divided into reward slices. Once the user taps
 * "Spin", we hit POST /api/daily/spin, the server returns the won reward,
 * we animate the wheel so the chosen slice lands at the top pointer, then
 * reveal the prize.
 */
export default function SpinWheel({ open, onClose, onAward }) {
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [angle, setAngle] = useState(0);
  const [result, setResult] = useState(null);
  const wheelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    api.get("/daily/spin/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [open]);

  if (!open) return null;
  const rewards = status?.rewards || [];
  const segCount = rewards.length || 7;
  const segAngle = 360 / segCount;

  const handleSpin = async () => {
    if (spinning || !status || status.claimed) return;
    setSpinning(true);
    try {
      const { data } = await api.post("/daily/spin");
      const idx = rewards.findIndex((r) => r.key === data.reward.key);
      // Land pointer (top, 12 o'clock) at the centre of the won segment.
      // We rotate the wheel so its segment centre aligns to angle 0 (top).
      // Segment i centre angle on the wheel = i*segAngle + segAngle/2 measured CW from 0.
      const target = 360 * 6 - (idx * segAngle + segAngle / 2);
      setAngle(target);
      setTimeout(() => {
        setSpinning(false);
        setResult(data.reward);
        goalConfetti();
        chime();
        onAward?.(data.reward);
      }, 4200);
    } catch (e) {
      setSpinning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm ps-fade-up" data-testid="spin-wheel-modal">
      <div className="ps-card-elev p-6 w-full max-w-sm relative overflow-hidden">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/5" data-testid="spin-close">
          <X size={18} className="text-slate-400" />
        </button>
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 ps-chip mb-2">
            <Sparkles size={11} /> Daily Spin
          </div>
          <div className="ps-heading text-2xl font-extrabold">
            Spin the <span className="ps-serif ps-italic font-normal">Ring</span>
          </div>
          <div className="text-xs text-slate-400 ps-serif ps-italic mt-1">
            One free spin every day. Keep coming back.
          </div>
        </div>

        {/* Wheel */}
        <div className="relative mx-auto mt-6 w-[260px] h-[260px]">
          {/* pointer */}
          <div className="absolute top-[-6px] left-1/2 -translate-x-1/2 z-10 w-0 h-0"
               style={{
                 borderLeft: "12px solid transparent",
                 borderRight: "12px solid transparent",
                 borderTop: "20px solid #FBBF24",
                 filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.4))",
               }} />
          <div
            ref={wheelRef}
            className="w-full h-full rounded-full relative overflow-hidden border-4 border-[#0E1F2A]"
            style={{
              transform: `rotate(${angle}deg)`,
              transition: spinning
                ? "transform 4.1s cubic-bezier(0.18, 0.9, 0.2, 1)"
                : "none",
              boxShadow: "0 30px 80px -20px rgba(74,222,128,0.45)",
            }}
          >
            {rewards.map((r, i) => {
              const rotate = i * segAngle;
              return (
                <div
                  key={r.key}
                  className="absolute top-0 left-1/2 origin-bottom-left"
                  style={{
                    width: "50%",
                    height: "50%",
                    transform: `rotate(${rotate}deg) skewY(${90 - segAngle}deg)`,
                    background: `linear-gradient(135deg, ${r.color} 0%, ${r.color}cc 100%)`,
                    borderRight: "1px solid rgba(255,255,255,0.15)",
                  }}
                >
                  <div
                    className="absolute text-[10px] font-extrabold uppercase tracking-wide text-[#050E15] whitespace-nowrap"
                    style={{
                      transform: `skewY(${-(90 - segAngle)}deg) rotate(${segAngle / 2}deg)`,
                      top: "22%",
                      left: "10%",
                      maxWidth: "90px",
                    }}
                  >
                    {r.label.replace("Posture Points", "Pts")}
                  </div>
                </div>
              );
            })}
            {/* hub */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#0E1F2A] border-2 border-[#4ade80] flex items-center justify-center">
              <Trophy size={20} color="#4ade80" />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6 text-center">
          {result || status?.last_reward ? (
            <div className="ps-card p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#4ade80] font-bold">You won</div>
              <div className="ps-heading text-2xl font-extrabold mt-1" data-testid="spin-result-label">
                {(result || status.last_reward).label}
              </div>
              <button onClick={onClose} className="ps-button-primary mt-4 w-full" data-testid="spin-claim-done">
                Awesome
              </button>
            </div>
          ) : status?.claimed ? (
            <div className="text-sm text-slate-400 ps-serif ps-italic">Come back tomorrow for another spin.</div>
          ) : (
            <button
              onClick={handleSpin}
              disabled={spinning}
              className="ps-button-primary w-full disabled:opacity-50"
              data-testid="spin-button"
            >
              {spinning ? "Spinning…" : "Spin now"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
